import { Calendar } from "lucide-react";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";
import { subDays, subMonths, startOfDay, endOfDay } from "date-fns";
import { useState } from "react";

import { Button } from "@/design-system/components/Button/Button";
import { Tooltip } from "@/design-system/components/Tooltip/Tooltip";
import { Popover } from "@/design-system/components/Popover/Popover";

export interface DateRange {
  start: Date;
  end: Date;
}

export type DateFilterType =
  | ({ type: "custom" } & DateRange)
  | { type: "today" }
  | { type: "yesterday" }
  | { type: "7DaysAgo" }
  | { type: "30DaysAgo" }
  | { type: "60DaysAgo" }
  | { type: "3MonthsAgo" }
  | { type: "6MonthsAgo" }
  | { type: "12MonthsAgo" };

export function getDateRangeFromFilter(filter: DateFilterType): DateRange {
  const now = new Date();
  const yesterday = subDays(now, 1);
  const end = endOfDay(yesterday);

  switch (filter.type) {
    case "custom":
      return { start: startOfDay(filter.start), end: endOfDay(filter.end) };
    case "today":
      return { start: startOfDay(now), end: endOfDay(now) };
    case "yesterday":
      return { start: startOfDay(yesterday), end };
    case "7DaysAgo":
      return { start: startOfDay(subDays(yesterday, 6)), end };
    case "30DaysAgo":
      return { start: startOfDay(subDays(yesterday, 29)), end };
    case "60DaysAgo":
      return { start: startOfDay(subDays(yesterday, 59)), end };
    case "3MonthsAgo":
      return { start: startOfDay(subMonths(now, 3)), end };
    case "6MonthsAgo":
      return { start: startOfDay(subMonths(now, 6)), end };
    case "12MonthsAgo":
      return { start: startOfDay(subMonths(now, 12)), end };
    default:
      return { start: startOfDay(subDays(yesterday, 6)), end };
  }
}

export type DateFilterOption = {
  type: Exclude<DateFilterType, { type: "custom" }>["type"];
  label: string;
  tooltip: string;
  disabled?: boolean;
};

interface DateFilterBaseProps {
  filter: DateFilterType;
  onChange: (newFilter: DateFilterType) => void;
  className?: string;
  tracking?: string;
  filterOptions?: DateFilterOption[];
}

interface DateFilterDefaultProps extends DateFilterBaseProps {
  variant?: "default";
  maxDaysBack?: number;
  maxDaysBackMessage?: string;
  label?: never;
}

interface DateFilterCompactProps extends DateFilterBaseProps {
  variant: "compact";
  label?: string;
  maxDaysBack?: never;
  maxDaysBackMessage?: never;
}

type DateFilterProps = DateFilterDefaultProps | DateFilterCompactProps;

const defaultFilterOptions = [
  { type: "yesterday" as const, label: "Yesterday", tooltip: "Yesterday only" },
  { type: "7DaysAgo" as const, label: "7D", tooltip: "Last 7 complete days" },
  {
    type: "30DaysAgo" as const,
    label: "30D",
    tooltip: "Last 30 complete days",
  },
  {
    type: "3MonthsAgo" as const,
    label: "3M",
    tooltip: "Last 3 complete months",
  },
  {
    type: "6MonthsAgo" as const,
    label: "6M",
    tooltip: "Last 6 complete months",
  },
  {
    type: "12MonthsAgo" as const,
    label: "12M",
    tooltip: "Last 12 complete months",
  },
];

function formatDateRange(dateRange: DateRange): string {
  const formatOptions: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  };
  const startStr = dateRange.start.toLocaleDateString("en-US", formatOptions);
  const endStr = dateRange.end.toLocaleDateString("en-US", formatOptions);
  return `${startStr} - ${endStr}`;
}

function CompactDateFilter({
  filter,
  onChange,
  className,
  filterOptions = defaultFilterOptions,
  tracking,
  label,
}: DateFilterCompactProps) {
  return (
    <div
      className={twMerge(clsx("inline-flex items-center gap-1.5", className))}
    >
      {label && <span className="text-textSubtle text-xs">{label}</span>}
      <div className="border-borderDefault bg-surface-minimal flex gap-0.5 rounded-full border p-0.5">
        {filterOptions.map((option) => (
          <button
            key={option.type}
            onClick={() => {
              if (option.disabled) return;
              onChange({ type: option.type });
            }}
            disabled={option.disabled}
            title={option.tooltip}
            data-track={tracking ? `${tracking}-${option.label}` : undefined}
            className={clsx(
              "cursor-pointer rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors",
              option.disabled
                ? "cursor-not-allowed opacity-50"
                : filter.type === option.type
                  ? "bg-blue-500 text-white dark:bg-blue-600"
                  : "text-textSubtle hover:bg-surface-subtle",
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function DateFilter(props: DateFilterProps) {
  if (props.variant === "compact") {
    return <CompactDateFilter {...props} />;
  }

  return <DefaultDateFilter {...props} />;
}

function DefaultDateFilter({
  filter,
  onChange,
  className,
  filterOptions = defaultFilterOptions,
  tracking,
  maxDaysBack,
  maxDaysBackMessage,
}: DateFilterDefaultProps) {
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);

  const handleCloseCustomDateRange = () => {
    setShowCustomDateRange(false);
  };

  const handleCustomDateRangeChange = (
    newStartDate: Date,
    newEndDate: Date,
  ) => {
    onChange({ type: "custom", start: newStartDate, end: newEndDate });
    handleCloseCustomDateRange();
  };

  const dateRange = getDateRangeFromFilter(filter);

  return (
    <div
      className={twMerge(
        clsx("inline-flex items-center gap-2 rounded-lg", className),
      )}
    >
      <Popover.Root
        open={showCustomDateRange}
        onOpenChange={setShowCustomDateRange}
      >
        <Popover.Trigger asChild>
          <Button
            variant="ghost"
            leadingIcon={<Calendar size={20} />}
            className={clsx(
              "h-10 px-2 py-1 text-sm font-semibold transition-colors",
              filter.type === "custom"
                ? "text-primary bg-surface-subtle"
                : "text-textSubtle",
            )}
            data-track={`${tracking}-custom`}
          >
            {filter.type === "custom" ? (
              <>
                {filter.start.toLocaleDateString("en-US", { timeZone: "UTC" })}{" "}
                - {filter.end.toLocaleDateString("en-US", { timeZone: "UTC" })}
              </>
            ) : (
              "Custom"
            )}
          </Button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={5}
            className="z-popover rounded-lg border border-gray-200 bg-white shadow-lg dark:bg-gray-100"
            onEscapeKeyDown={handleCloseCustomDateRange}
            onPointerDownOutside={handleCloseCustomDateRange}
          >
            <DateRangeInput
              startDate={
                filter.type === "custom" ? filter.start : subDays(new Date(), 7)
              }
              endDate={
                filter.type === "custom" ? filter.end : subDays(new Date(), 1)
              }
              onCancel={handleCloseCustomDateRange}
              onChange={handleCustomDateRangeChange}
              maxDaysBack={maxDaysBack}
              maxDaysBackMessage={maxDaysBackMessage}
            />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {filterOptions.map((option) => (
        <Tooltip key={option.type} content={option.tooltip}>
          <Button
            variant="ghost"
            onClick={() => {
              if (option.disabled) return;
              handleCloseCustomDateRange();
              onChange({ type: option.type });
            }}
            disabled={option.disabled}
            className={clsx(
              "h-10 px-2 py-1 text-sm font-semibold transition-colors",
              option.disabled
                ? "cursor-not-allowed opacity-50"
                : filter.type === option.type
                  ? "text-primary bg-surface-subtle"
                  : "text-textSubtle",
            )}
            data-track={`${tracking}-${option.label}`}
          >
            {option.label}
          </Button>
        </Tooltip>
      ))}
      {filter.type !== "custom" && (
        <span className="text-textSubtle ml-2 text-sm">
          {formatDateRange(dateRange)}
        </span>
      )}
    </div>
  );
}

interface DateRangeInputProps {
  startDate: Date;
  endDate: Date;
  onCancel: () => void;
  onChange: (startDate: Date, endDate: Date) => void;
  maxDaysBack?: number;
  maxDaysBackMessage?: string;
}

function isValidDate(date: Date): boolean {
  return date instanceof Date && !isNaN(date.getTime());
}

// A YYYY-MM-DD string from `<input type="date">` identifies a calendar day
// the user picked in their local timezone. `new Date("2026-04-10")` parses
// that as UTC midnight — which is the previous calendar day west of UTC —
// and any later `startOfDay`/`endOfDay` would snap to that wrong day.
function parseLocalDateInput(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [, y, m, d] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return isValidDate(date) ? date : null;
}

function formatDateForInput(date: Date): string {
  if (!isValidDate(date)) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function DateRangeInput({
  startDate,
  endDate,
  onCancel,
  onChange,
  maxDaysBack,
  maxDaysBackMessage,
}: DateRangeInputProps) {
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const [endDateError, setEndDateError] = useState<string | null>(null);

  const yesterday = subDays(new Date(), 1).toISOString().split("T")[0];
  const minDate = maxDaysBack
    ? subDays(new Date(), maxDaysBack).toISOString().split("T")[0]
    : undefined;

  const startDateMax = formatDateForInput(localEndDate) || yesterday;
  const endDateMin = formatDateForInput(localStartDate) || minDate;

  const minAllowedDate = maxDaysBack ? subDays(new Date(), maxDaysBack) : null;

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!value) {
      setStartDateError("Start date is required");
      return;
    }
    const newDate = parseLocalDateInput(value);
    if (newDate === null) {
      setStartDateError("Invalid date");
      return;
    }

    const normalizedNewDate = startOfDay(newDate);

    // Check if start date is before the minimum allowed date
    if (minAllowedDate && normalizedNewDate < startOfDay(minAllowedDate)) {
      setStartDateError(
        `Start date cannot be earlier than ${maxDaysBack} days ago`,
      );
      setLocalStartDate(newDate); // Still update so UI reflects selection
      return;
    }
    // Check if start date is after end date
    if (
      isValidDate(localEndDate) &&
      normalizedNewDate > startOfDay(localEndDate)
    ) {
      setStartDateError("Start date cannot be after end date");
      setLocalStartDate(newDate); // Still update so UI reflects selection
      return;
    }

    // Valid date - clear error and update
    setStartDateError(null);
    setLocalStartDate(newDate);
    // Clear end date error if it was due to being before start date
    if (endDateError === "End date cannot be before start date") {
      setEndDateError(null);
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!value) {
      setEndDateError("End date is required");
      return;
    }
    const newDate = parseLocalDateInput(value);
    if (newDate === null) {
      setEndDateError("Invalid date");
      return;
    }

    const normalizedNewDate = startOfDay(newDate);

    // Check if end date is before start date
    if (
      isValidDate(localStartDate) &&
      normalizedNewDate < startOfDay(localStartDate)
    ) {
      setEndDateError("End date cannot be before start date");
      setLocalEndDate(newDate); // Still update so UI reflects selection
      return;
    }
    // Check if end date is after yesterday
    const yesterdayDate = subDays(new Date(), 1);
    if (normalizedNewDate > endOfDay(yesterdayDate)) {
      setEndDateError("End date cannot be in the future");
      setLocalEndDate(newDate); // Still update so UI reflects selection
      return;
    }

    // Valid date - clear error and update
    setEndDateError(null);
    setLocalEndDate(newDate);
    // Clear start date error if it was due to being after end date
    if (startDateError === "Start date cannot be after end date") {
      setStartDateError(null);
    }
  };

  const hasErrors = !!startDateError || !!endDateError;
  const hasValidDates =
    isValidDate(localStartDate) && isValidDate(localEndDate);

  const handleApply = () => {
    if (hasValidDates && !hasErrors) {
      onChange(startOfDay(localStartDate), endOfDay(localEndDate));
    }
  };

  return (
    <div className="min-w-[300px] space-y-4 p-4">
      {maxDaysBackMessage && (
        <p className="text-textSubtle text-xs">{maxDaysBackMessage}</p>
      )}
      <div className="space-y-2">
        <label
          htmlFor="date-filter-start"
          className="text-textDefault text-sm font-medium"
        >
          Start Date
        </label>
        <input
          id="date-filter-start"
          type="date"
          value={formatDateForInput(localStartDate)}
          min={minDate}
          max={startDateMax}
          onChange={handleStartDateChange}
          className={clsx(
            "w-full rounded-md border px-3 py-2 text-sm focus:ring-1 focus:outline-none",
            startDateError
              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
              : "border-borderDefault focus:border-primary focus:ring-primary",
          )}
        />
        {startDateError && (
          <p className="text-xs text-red-500">{startDateError}</p>
        )}
      </div>
      <div className="space-y-2">
        <label
          htmlFor="date-filter-end"
          className="text-textDefault text-sm font-medium"
        >
          End Date
        </label>
        <input
          id="date-filter-end"
          type="date"
          value={formatDateForInput(localEndDate)}
          min={endDateMin}
          max={yesterday}
          onChange={handleEndDateChange}
          className={clsx(
            "w-full rounded-md border px-3 py-2 text-sm focus:ring-1 focus:outline-none",
            endDateError
              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
              : "border-borderDefault focus:border-primary focus:ring-primary",
          )}
        />
        {endDateError && <p className="text-xs text-red-500">{endDateError}</p>}
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} className="h-10">
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          className="h-10"
          disabled={hasErrors || !hasValidDates}
        >
          Apply
        </Button>
      </div>
    </div>
  );
}
